import React, { useCallback, useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { AccommodationsSectionLayoutType } from '@shared/types/Accommodations.types';

// In-memory overrides for the accommodations section, set by PreviewListener when the
// admin postMessages a new value. Stay in app state — never written to Apollo cache —
// so we don't pollute BE-truth with preview-only values. In production guest viewing,
// no admin sends messages and the overrides stay `null`, falling through to the cached
// saved values.
type AccommodationsLayoutOverrideStore = Readonly<{
  layoutOverride: AccommodationsSectionLayoutType | null;
  setLayoutOverride: (layout: AccommodationsSectionLayoutType | null) => void;
  // Preview-only override for the Neighborhoods section intro note. `null` = no override.
  noteOverride: string | null;
  setNoteOverride: (note: string | null) => void;
}>;

const noop = () => {};

const DEFAULT_STORE: AccommodationsLayoutOverrideStore = {
  layoutOverride: null,
  setLayoutOverride: noop,
  noteOverride: null,
  setNoteOverride: noop,
};

const [Provider, useStore] = createContext<AccommodationsLayoutOverrideStore>({
  name: 'AccommodationsLayoutOverrideContext',
  // No-op default so consumers (like the Accommodations widget) work even when the
  // provider isn't mounted — important for any guest render path that bypasses GuestSiteBody.
  strict: false,
  initialValue: DEFAULT_STORE,
});

export const useAccommodationsLayoutOverride = (): AccommodationsLayoutOverrideStore => useStore(false) ?? DEFAULT_STORE;

export const AccommodationsLayoutOverrideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [layoutOverride, setLayoutOverride] = useState<AccommodationsSectionLayoutType | null>(null);
  const [noteOverride, setNoteOverride] = useState<string | null>(null);
  const setLayout = useCallback((layout: AccommodationsSectionLayoutType | null) => {
    setLayoutOverride(layout);
  }, []);
  const setNote = useCallback((note: string | null) => {
    setNoteOverride(note);
  }, []);
  const value = useMemo(() => ({ layoutOverride, setLayoutOverride: setLayout, noteOverride, setNoteOverride: setNote }), [layoutOverride, setLayout, noteOverride, setNote]);
  return <Provider value={value}>{children}</Provider>;
};
